<script setup lang="ts">
const route = useRoute()
const { env } = useCoreConfig()
const { isLoggedIn, userJustLoggedIn } = useUserInfo()
const { consentCode } = useRecurringConsent()
const { isActive: isTermsDialogActive } = useTermsDialog()
const { isModalOpen: isBonusPlayModalOpen } = useBonusPlayCheck()
// const { showAppPrompt } = useAppPrompt()

// Snapshot on purpose: a live cookie ref re-triggers the recurring-consent double-fire.
const isUserJustLoggedIn = userJustLoggedIn.value

// Latched: saving clears consentCode, which would otherwise unmount the step mid-save.
const recurringConsentMounted = ref(false)
const showRecurringConsent = computed(() => isUserJustLoggedIn || !!consentCode.value)
whenever(showRecurringConsent, () => {
    recurringConsentMounted.value = true
}, { immediate: true })

// PaymentsReceipt
const { receiptTransaction } = useDepositReceipt({ followRoute: true })

const { isMobileApp, isMobileAppAndRequiresUpdate } = useMobileApp()
usePasskeyLoginFlow()

const allowMoneyLimitsDialog = computed(() => {
    if (route.name === 'konto-indbetalingsloft') return false

    return true
})

// Latched: the auth sheet animates out after ?auth has left the URL, so the container stays until it reports closed.
const isAuthMounted = ref(false)
whenever(() => !!route.query.auth, () => {
    isAuthMounted.value = true
}, { immediate: true })

// Latched like auth: the terms animate out after their link has left the URL. They report closed before
// router.back() has landed, so the latch drops on closed alone; the next link latches it again.
const isTermsMounted = ref(false)
whenever(() => !!isTermsDialogActive.value, () => {
    isTermsMounted.value = true
}, { immediate: true })
</script>

<template>
    <div class="overlay-container">
        <!-- Dialogs available after login -->
        <template v-if="isLoggedIn">
            <!-- Dialogs that show notifications -->
            <NimbusNotificationDialogContainer />
            <!-- ./ Dialogs that show notifications -->

            <!-- Dialogs that show promotions -->
            <template v-if="!route.meta.hidePromotions">
                <LazyNimbusRecurringConsentContainer
                    v-if="recurringConsentMounted"
                />
            </template>
            <!-- ./ Dialogs that show promotions -->
            <LazyPaymentsDepositReceipt
                v-if="receiptTransaction"
                :key="receiptTransaction.txId"
                :tx-id="receiptTransaction.txId"
                :psp="receiptTransaction.psp"
                z-index="21"
            /> <!-- z-index refers to take-over -->
            <ConsentChangedContainer />
            <!-- <ClientOnly>
                <LazyNimbusMobileAppInstallPrompt
                    v-if="!isMobileApp && showAppPrompt"
                    hydrate-on-idle
                    :title="'Hent vores nye app'"
                    :description="'Ny app, nye eventyr! Hent Spilnu.dk appen i dag! Klik på knappen ⤵️'"
                    :maybe-later="'Måske senere'"
                    :logo="{ src: '/media/logo/512x512.png', alt: 'Spilnu.dk logo' }"
                    :android="{ ...app.android, alt: 'Download Spilnu.dk i Google Play Butik' }"
                    :ios="{ ...app.ios, alt: 'Download Spilnu.dk i App Store' }"
                />
            </ClientOnly> -->

            <LazyPaymentsMoneyLimitsDialog v-if="allowMoneyLimitsDialog" />

            <LazyBonusPlayModal v-if="isBonusPlayModalOpen" />
        </template>

        <!-- Dialogs available before login -->
        <template v-else>
            <!-- Idle hydration: on ?auth= landings the dialog otherwise hydrates in the same pass as the lobby (RUM: ~4x the plain-lobby TBT). -->
            <LazyIdleAuthContainer v-if="isAuthMounted" hydrate-on-idle @closed="isAuthMounted = !!route.query.auth" />
        </template>

        <!-- Mobile App Version dialog -->
        <ClientOnly v-if="isMobileAppAndRequiresUpdate">
            <LazyIdleNimbusMobileAppVersionDialog hydrate-on-idle />
        </ClientOnly>

        <!-- LoggedOutReason dialog -->
        <LazyUserLoggedOutMessage v-if="!isLoggedIn" />

        <!-- Dialogs always available -->
        <LazyPromotionTermsContainer v-if="isTermsMounted" @closed="isTermsMounted = false" />
        <LazyEnvBanner v-if="env !== 'prod' && !route.meta.hideEnvBanner" />
    </div>
</template>
